import { Card, CardContent, CardFooter, CardHeader } from 'components/ui/card';
import { FunnelIcon } from 'lucide-react';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormField } from 'components/ui/form';
import React, { useCallback } from 'react';
import { InputGs } from 'components/ui/Forms';
import { Button as ButtonGs } from 'components/ui/ButtonGs';
import DatePickerGs from 'components/ui/Forms/datePickerGs';
import { useFormSchema } from './useFormSchema';

type Props = React.ComponentPropsWithoutRef<typeof Card> & {
  onFilter: Function;
  query: any;
};

export const ChecklistFilter = ({
  onFilter,
  children,
  query,
  ...props
}: Props) => {
  const [open, setOpen] = React.useState(false);
  const toggleOpen = useCallback(() => setOpen(!open), [open]);

  const formSchema = useFormSchema();
  type FormSchema = z.infer<ReturnType<typeof useFormSchema>>;

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const onFormSubmit = useCallback(
    (data: FormSchema) => {
      const filters = {
        ...data,
        started_at: (data as any).started_at
          ? (data as any).started_at.toISOString().slice(0, 10)
          : undefined,
        finished_at: (data as any).finished_at
          ? (data as any).finished_at.toISOString().slice(0, 10)
          : undefined,
      };
      Object.keys(filters).forEach(
        (k) =>
          ((filters as any)[k] === '' || (filters as any)[k] === undefined) &&
          delete (filters as any)[k]
      );
      onFilter(filters);
      toggleOpen();
    },
    [onFilter, toggleOpen]
  );

  const onFormReset = useCallback(() => {
    form.reset({
      id: '',
      checklist_user_name: '',
      started_at: undefined,
      finished_at: undefined,
    });
    onFilter({});
  }, [form, onFilter]);

  return (
    <>
      <Card
        {...props}
        className={cn(open ? 'rounded-b-md rounded-t-none' : 'rounded-none')}
      >
        <CardHeader className="flex flex-row gap-3 items-center justify-between">
          {children}
          <Button
            variant="outline"
            onClick={toggleOpen}
            className="w-[42px] p-0"
          >
            <FunnelIcon className={cn('', open && 'fill-black')} />
          </Button>
        </CardHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onFormSubmit)}>
            <CardContent className={cn('hidden', open && 'flex flex-col')}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {/* Filtro por ID */}
                <FormField
                  control={form.control}
                  name="id"
                  render={({ field }) => (
                    <InputGs
                      {...field}
                      label="ID"
                      onChange={(e) => field.onChange(e.target.value)}
                      value={field.value || ''}
                    />
                  )}
                />
                {/* Nome do usuário */}
                <FormField
                  control={form.control}
                  name="checklist_user_name"
                  render={({ field }) => (
                    <InputGs
                      {...field}
                      label="Usuário"
                      onChange={(e) => field.onChange(e.target.value)}
                      value={field.value || ''}
                    />
                  )}
                />
                {/* Data Inicial */}
                <FormField
                  name="started_at"
                  control={form.control}
                  render={({ field }) => (
                    <DatePickerGs
                      {...field}
                      label="Início Checklist"
                      fieldValue={field.value}
                      fieldOnChange={field.onChange}
                    />
                  )}
                />
                {/* Data Final */}
                <FormField
                  name="finished_at"
                  control={form.control}
                  render={({ field }) => (
                    <DatePickerGs
                      {...field}
                      label="Conclusão Checklist"
                      fieldValue={field.value}
                      fieldOnChange={field.onChange}
                    />
                  )}
                />
              </div>
            </CardContent>
            <CardFooter className={cn('hidden gap-4', open && 'flex')}>
              <ButtonGs.Root
                type="button"
                variant="secondary"
                size="sm"
                onClick={onFormReset}
              >
                Limpar
              </ButtonGs.Root>
              <ButtonGs.Root type="submit" size="sm">
                Filtrar
              </ButtonGs.Root>
            </CardFooter>
          </form>
        </Form>
      </Card>
    </>
  );
};
